<template>
  <CustomModal ref="modal" :title="modalTitle" class="purchase-modal">
    <div class="container">
      <div v-if="isEdit" class="summary">
        <StatusBadge :status="purchase.status" :status-enum="statusEnum" />
        <StatusBadge v-if="purchase.deliveryStatus" :status="purchase.deliveryStatus" :status-enum="DELIVERY_STATUS_ENUM" />
        <span v-if="purchase.incomingInvoice" class="source">
          <span class="fas fa-file-invoice"></span>
          {{ isCreditNote ? 'Avoir reçu' : 'Facture reçue' }}<template v-if="purchase.incomingInvoice.number"> n° {{ purchase.incomingInvoice.number }}</template>
        </span>
        <button v-if="purchase.incomingInvoice && purchase.incomingInvoice.providerInvoiceId" type="button" class="link-button" @click="download">Télécharger {{ isCreditNote ? "l'avoir" : 'la facture' }}</button>
        <span class="paid">
          {{ isCreditNote ? 'Remboursé' : 'Payé' }} : {{ formatAmount(purchase.paidAmount) }} sur {{ formatAmount(purchase.ttcAmount) }}
          <template v-if="purchase.offsetAmount > 0"> · {{ isCreditNote ? 'déduit de la facture' : 'avoirs déduits' }} : {{ formatAmount(purchase.offsetAmount) }}</template>
        </span>
      </div>
      <p v-if="isToReview" class="review-note">
        Cet achat vient d'une facture reçue : vérifiez les informations, complétez la catégorie et les dossiers concernés, puis validez-le pour pouvoir enregistrer son {{ isCreditNote ? 'remboursement' : 'paiement' }}.
      </p>

      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <div class="supplier-row">
          <CustomSelectField class="supplier" :content=inputs.supplierId />
          <button type="button" class="button-icon add-supplier" title="Nouveau fournisseur" @click="$refs.supplierModal.openModal()"><span class="fas fa-plus"></span></button>
        </div>
        <div class="fields-row">
          <CustomTextField :content=inputs.reference />
          <CustomTextField :content=inputs.label />
        </div>
        <div class="fields-row">
          <CustomTextField :content=inputs.invoiceDate />
          <CustomTextField v-if="!isCreditNote" :content=inputs.dueDate />
        </div>
        <p v-if="isVatFree" class="vat-note"><span class="fas fa-circle-info"></span>À cette date, le groupe est sans TVA (franchise en base) : la TVA de cette {{ isCreditNote ? 'pièce' : 'facture' }} n'est pas récupérable, le coût pris en compte dans les marges et la comptabilité est le montant TTC.</p>
        <CustomSelectField :content=inputs.category />
        <CustomSelectField v-if="isCreditNote" :content=inputs.creditedPurchaseId />

        <div class="section">
          <div class="section-header">
            <p class="section-label">Articles <span class="hint">(facultatif)</span></p>
            <button v-if="!showLines" type="button" class="link-button" @click="showLines = true">+ Détailler par articles</button>
          </div>
          <p v-if="!showLines" class="hint">Les quantités permettent de suivre le stock, la livraison et de ventiler les matériaux sur les dossiers.</p>
          <PurchaseLinesEditor v-show="showLines" ref="linesEditor" :credited-lines="creditedLines" :callback="onLinesChanged" />
        </div>

        <div class="section">
          <p class="section-label">Montants</p>
          <template v-if="linesState.count === 0">
            <div class="line-row line-header">
              <span>Taux de TVA</span>
              <span>Montant HT (€)</span>
              <span>Montant TVA (€)</span>
              <span></span>
            </div>
            <div v-for="(line, index) in lines" :key="line.uid" class="line-row">
              <CustomSelectField :content=line.rate />
              <CustomTextField :content=line.htAmount />
              <CustomTextField :content=line.vatAmount />
              <button v-if="lines.length > 1" type="button" class="button-icon-custom" title="Retirer la ligne" @click="removeLine(index)"><span class="fas fa-xmark"></span></button>
              <span v-else></span>
            </div>
            <button type="button" class="link-button" @click="addLine">+ Ajouter un taux de TVA</button>
          </template>
          <p v-else class="hint">Calculés d'après les articles.</p>
          <p class="totals">
            HT {{ formatAmount(displayTotals.ht) }} · TVA {{ formatAmount(displayTotals.vat) }} · <strong>TTC {{ formatAmount(displayTotals.ttc) }}</strong>
          </p>
        </div>

        <div v-if="hasProduction" class="section">
          <p class="section-label">Dossiers concernés <span class="hint">(facultatif)</span></p>
          <AllocationEditor ref="allocationEditor" :order-values="orderValues" :lines="linesState.lines" :total-ht="displayTotals.ht" />
        </div>
        <div v-else-if="readonlyAllocations.length > 0" class="section">
          <p class="section-label">Dossiers concernés</p>
          <p v-for="allocation in readonlyAllocations" :key="allocation.key" class="readonly-allocation">
            {{ allocation.label }} : {{ formatAmount(allocation.htAmount) }} HT
          </p>
        </div>

        <CustomTextField :content=inputs.notes />

        <div v-if="isEdit && purchase.deliveryStatus" class="section">
          <div class="section-header">
            <p class="section-label">Livraison</p>
            <button type="button" class="link-button" @click="receive">+ Enregistrer une réception</button>
          </div>
          <table v-if="receptions.length > 0" class="mini-table">
            <tbody>
              <tr v-for="reception in receptions" :key="reception.id" title="Modifier la réception" @click="editReception(reception)">
                <td>{{ formatDate(reception.date) }}</td>
                <td class="fill">{{ reception.reference || '' }}</td>
                <td class="amount">{{ receptionSummary(reception) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="hint">Aucune réception enregistrée</p>
        </div>

        <div v-if="isEdit" class="section">
          <div class="section-header">
            <p class="section-label">{{ isCreditNote ? 'Remboursements' : 'Décaissements' }}</p>
            <button v-if="canPay" type="button" class="link-button" @click="pay">+ Enregistrer un {{ isCreditNote ? 'remboursement' : 'paiement' }}</button>
          </div>
          <table v-if="disbursements.length > 0" class="mini-table">
            <tbody>
              <tr v-for="disbursement in disbursements" :key="disbursement.id" :title="isCreditNote ? 'Modifier le remboursement' : 'Modifier le décaissement'" @click="editDisbursement(disbursement)">
                <td>{{ formatDate(disbursement.date) }}</td>
                <td>{{ labelOf(DISBURSEMENT_PAYMENT_TYPE_ENUM, disbursement.paymentType) }}</td>
                <td class="fill">{{ disbursement.reference || '' }}</td>
                <td class="amount">{{ formatAmount(disbursement.amount) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="hint">{{ isCreditNote ? 'Aucun remboursement enregistré' : 'Aucun décaissement enregistré' }}</p>
        </div>

        <div v-if="isEdit && !isCreditNote" class="section">
          <div class="section-header">
            <p class="section-label">Avoirs</p>
            <button type="button" class="link-button" @click="createCreditNote">+ Créer un avoir</button>
          </div>
          <table v-if="creditNotes.length > 0" class="mini-table">
            <tbody>
              <tr v-for="note in creditNotes" :key="note.id" title="Ouvrir l'avoir" @click="openModal(note)">
                <td>{{ formatDate(note.invoiceDate) }}</td>
                <td class="fill">{{ note.reference || note.label }}</td>
                <td><StatusBadge :status="note.status" :status-enum="PURCHASE_CREDIT_NOTE_STATUS_ENUM" /></td>
                <td class="amount">{{ formatAmount(-note.ttcAmount) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="hint">Aucun avoir : retour de marchandises, remise obtenue après coup...</p>
        </div>

        <div v-if="isEdit" class="section">
          <p class="section-label">Justificatifs</p>
          <PurchaseProofs :purchase-id="purchaseId" :proofs="purchase.proofs" :callback="onProofsChanged" />
        </div>

        <div class="button-container">
          <CustomButton class="button" @click="submitForm(false)" :params=buttons.submit />
          <CustomButton v-if="isToReview" class="button validate" @click="submitForm(true)" :params=validateParams />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
  <DisbursementModal ref="disbursementModal" :callback="onDisbursementChanged" />
  <ReceptionModal ref="receptionModal" :callback="onReceptionChanged" />
  <SupplierModal ref="supplierModal" :callback="onSupplierCreated" />
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleInfo, faFileInvoice, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import {NotificationService} from "@vues/v-common";
import {convertISOToDate} from "@/utils";
import {PurchaseController} from "@/purchase/controllers/PurchaseController";
import {SupplierController} from "@/purchase/controllers/SupplierController";
import {DisbursementController} from "@/purchase/controllers/DisbursementController";
import {ReceptionController} from "@/purchase/controllers/ReceptionController";
import {downloadIncomingInvoice} from "@/purchase/services/PurchaseDocumentService";
import {loadOrderValues} from "@/purchase/services/OrderChoiceService";
import {loadVatRegimes} from "@/purchase/services/VatRegimeService";
import {isSubjectToVatOnDay} from "@/purchase/vatRegime";
import AllocationEditor from "@/purchase/components/AllocationEditor.vue";
import PurchaseLinesEditor from "@/purchase/components/PurchaseLinesEditor.vue";
import PurchaseProofs from "@/purchase/components/PurchaseProofs.vue";
import DisbursementModal from "@/purchase/components/modals/DisbursementModal.vue";
import ReceptionModal from "@/purchase/components/modals/ReceptionModal.vue";
import SupplierModal from "@/purchase/components/modals/SupplierModal.vue";
import {
  DELIVERY_STATUS_ENUM,
  DISBURSEMENT_PAYMENT_TYPE_ENUM,
  PURCHASE_CATEGORY_ENUM,
  PURCHASE_CREDIT_NOTE_STATUS_ENUM,
  PURCHASE_DOCUMENT_TYPE_ENUM,
  PURCHASE_STATUS_ENUM,
  dueDateFrom,
  enumToSelectValues,
  formatAmount,
  formatDate,
  formatQuantity,
  idOf,
  labelOf,
  round2,
  statusEnumOf,
  toInputDate,
  vatRateSelectValues
} from "@/purchase/utils";
library.add(faCircleInfo, faFileInvoice, faPlus, faXmark);

const { INVOICE, CREDIT_NOTE } = PURCHASE_DOCUMENT_TYPE_ENUM;
const NO_INVOICE = {label: 'Aucune (remise, ristourne...)', value: ''};
const EMPTY_LINES_STATE = () => ({count: 0, totals: {ht: 0, vat: 0, ttc: 0}, isDirty: false, lines: []});

// Modale d'un achat (facture ou avoir fournisseur) : création avec openModal(), consultation / modification avec openModal(achat),
// nouvel avoir avec openModal(undefined, undefined, {creditNoteOf: facture}) ou {documentType: 'CREDIT_NOTE'}.
// En modification, elle liste aussi les décaissements, réceptions, avoirs et justificatifs de l'achat.
// callback est appelé après chaque enregistrement (achat, décaissement, réception, justificatif).
export default {
  name: 'PurchaseModal',
  props: ['callback'],
  components: {SupplierModal, DisbursementModal, ReceptionModal, StatusBadge, CustomModal, CustomForm, CustomSelectField, CustomTextField, CustomButton, AllocationEditor, PurchaseLinesEditor, PurchaseProofs},
  data() {
    return {
      purchaseId: undefined,
      purchase: undefined,        // achat enregistré (modification) : statut, montants payés, facture reçue...
      documentType: INVOICE.value,
      disbursements: [],
      receptions: [],
      creditNotes: [],            // avoirs de la facture
      suppliers: [],
      orderValues: [],            // dossiers proposés pour la ventilation
      vatRegimes: [],             // historique du régime de TVA du groupe
      hasProduction: false,
      // Valeurs proposées d'après le fournisseur (catégorie, échéance) tant que l'utilisateur ne les a pas modifiées
      autoFill: true,
      autoCategory: PURCHASE_CATEGORY_ENUM.OTHER.value,
      autoDueDate: '',
      lines: [],                  // lignes de TVA saisies (achat sans articles)
      showLines: false,
      linesState: EMPTY_LINES_STATE(),    // articles saisis, transmis par l'éditeur d'articles
      creditedLines: [],          // articles de la facture visée par un avoir
      uidCounter: 0,
      inputs: {
        supplierId: {label: 'Fournisseur', value: undefined, type: 'select', selectValues: [], required: true},
        reference: {label: 'N° de facture', value: '', type: 'text'},
        label: {label: 'Libellé', value: '', type: 'text'},
        invoiceDate: {label: 'Date de la facture', value: convertISOToDate(new Date()), type: 'date', required: true},
        dueDate: {label: 'Échéance', value: '', type: 'date'},
        category: {label: 'Catégorie', value: PURCHASE_CATEGORY_ENUM.OTHER.value, type: 'select', selectValues: enumToSelectValues(PURCHASE_CATEGORY_ENUM), required: true},
        creditedPurchaseId: {label: 'Facture concernée', value: '', type: 'select', selectValues: [NO_INVOICE]},
        notes: {label: 'Notes', value: '', type: 'text'},
      },
      buttons: {
        submit: {text: "Enregistrer"},
        validate: {text: "Enregistrer et valider", disabled: false},
        cancel: {text: "Annuler"},
      },
      PURCHASE_CREDIT_NOTE_STATUS_ENUM,
      DELIVERY_STATUS_ENUM,
      DISBURSEMENT_PAYMENT_TYPE_ENUM
    };
  },
  computed: {
    isEdit() {
      return this.purchaseId !== undefined && this.purchase !== undefined;
    },
    isCreditNote() {
      return this.documentType === CREDIT_NOTE.value;
    },
    // La date de la pièce tombe dans une période où le groupe ne facture pas de TVA
    isVatFree() {
      return !!this.inputs.invoiceDate.value && !isSubjectToVatOnDay(this.vatRegimes, this.inputs.invoiceDate.value);
    },
    statusEnum() {
      return statusEnumOf({documentType: this.documentType});
    },
    modalTitle() {
      const name = this.isCreditNote ? 'avoir' : 'achat';
      return this.isEdit ? (this.isCreditNote ? 'Avoir' : 'Achat') : `Nouvel ${name}`;
    },
    isToReview() {
      return this.isEdit && this.purchase.status === PURCHASE_STATUS_ENUM.TO_REVIEW.value;
    },
    remainingAmount() {
      return this.isEdit ? round2(this.purchase.ttcAmount - (this.purchase.paidAmount ?? 0) - (this.purchase.offsetAmount ?? 0)) : 0;
    },
    canPay() {
      return this.isEdit && !this.isToReview && this.remainingAmount > 0;
    },
    // Le bouton « Enregistrer et valider » suit l'état du bouton principal (formulaire incomplet)
    validateParams() {
      return {...this.buttons.validate, disabled: this.buttons.validate.disabled || this.buttons.submit.disabled};
    },
    vatTotals() {
      const cents = value => Math.round(this.toNumber(value) * 100);
      const ht = this.lines.reduce((sum, line) => sum + cents(line.htAmount.value), 0);
      const vat = this.lines.reduce((sum, line) => sum + cents(line.vatAmount.value), 0);
      return {ht: ht / 100, vat: vat / 100, ttc: (ht + vat) / 100};
    },
    // Montants de l'achat : ceux des articles s'il y en a, sinon des lignes de TVA saisies.
    // Des articles enregistrés et inchangés gardent les montants enregistrés (la TVA d'une facture reçue fait foi).
    displayTotals() {
      if (this.linesState.count === 0) return this.vatTotals;
      if (!this.linesState.isDirty && this.purchase) return {ht: this.purchase.htAmount, vat: this.purchase.vatAmount, ttc: this.purchase.ttcAmount};
      return this.linesState.totals;
    },
    // Sans l'option production, la ventilation enregistrée reste visible mais ne se modifie pas
    readonlyAllocations() {
      return (this.purchase?.allocations ?? []).map((allocation, index) => ({
        key: index,
        label: `N°${allocation.orderNumber ?? '?'}${allocation.orderTitle ? ` - ${allocation.orderTitle}` : ''}`,
        htAmount: allocation.htAmount,
      }));
    },
  },
  watch: {
    "inputs.supplierId.value"() {
      this.applyDefaults();
      this.loadInvoices();
    },
    "inputs.invoiceDate.value"() {
      this.applyDefaults();
    },
    "inputs.creditedPurchaseId.value"() {
      this.loadCreditedLines();
    },
    // Le montant de TVA d'une ligne est recalculé quand son taux ou son montant HT change ;
    // un montant de TVA saisi à la main (ou repris d'une facture reçue) est conservé tant qu'ils ne bougent pas.
    lines: {
      deep: true,
      handler() {
        this.lines.forEach(line => {
          const key = `${line.rate.value}|${line.htAmount.value}`;
          if (line.key === key) return;
          line.key = key;
          line.vatAmount.value = String(line.htAmount.value ?? '').trim() === ''
              ? ''
              : round2(this.toNumber(line.htAmount.value) * this.toNumber(line.rate.value) / 100);
        });
      }
    }
  },
  methods: {
    formatAmount,
    formatDate,
    labelOf,
    toNumber(value) {
      const number = Number(String(value ?? '').replace(',', '.'));
      return isNaN(number) ? 0 : number;
    },
    isBlank(value) {
      return String(value ?? '').trim() === '';
    },
    nextUid() {
      this.uidCounter++;
      return this.uidCounter;
    },
    newLine({rate = 20, htAmount = '', vatAmount = ''} = {}) {
      return {
        uid: this.nextUid(),
        key: `${rate}|${htAmount}`,
        rate: {value: rate, type: 'select', selectValues: vatRateSelectValues(rate)},
        htAmount: {value: htAmount, type: 'number', step: 0.01},
        vatAmount: {value: vatAmount, type: 'number', step: 0.01},
      };
    },
    addLine() {
      this.lines.push(this.newLine());
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    onLinesChanged(state) {
      this.linesState = state;
    },
    setDocumentType(documentType) {
      this.documentType = documentType;
      const isCreditNote = documentType === CREDIT_NOTE.value;
      this.inputs.reference.label = isCreditNote ? "N° d'avoir" : 'N° de facture';
      this.inputs.invoiceDate.label = isCreditNote ? "Date de l'avoir" : 'Date de la facture';
    },
    // Catégorie et échéance proposées d'après le fournisseur choisi (création uniquement)
    applyDefaults() {
      if (!this.autoFill) return;
      const supplier = this.suppliers.find(item => idOf(item) === this.inputs.supplierId.value);
      if (!supplier) return;

      const category = supplier.defaultCategory || PURCHASE_CATEGORY_ENUM.OTHER.value;
      if (this.inputs.category.value === this.autoCategory) this.inputs.category.value = category;
      this.autoCategory = category;

      // Un avoir n'a pas d'échéance
      if (this.isCreditNote) return;
      const dueDate = dueDateFrom(this.inputs.invoiceDate.value, supplier.paymentTermDays);
      if (this.inputs.dueDate.value === this.autoDueDate) this.inputs.dueDate.value = dueDate;
      this.autoDueDate = dueDate;
    },
    resetState(documentType) {
      this.setDocumentType(documentType);
      this.purchaseId = undefined;
      this.purchase = undefined;
      this.disbursements = [];
      this.receptions = [];
      this.creditNotes = [];
      this.autoFill = true;
      this.autoCategory = PURCHASE_CATEGORY_ENUM.OTHER.value;
      this.autoDueDate = '';
      this.inputs.supplierId.value = undefined;
      this.inputs.reference.value = '';
      this.inputs.label.value = '';
      this.inputs.invoiceDate.value = convertISOToDate(new Date());
      this.inputs.dueDate.value = '';
      this.inputs.category.value = PURCHASE_CATEGORY_ENUM.OTHER.value;
      this.inputs.creditedPurchaseId.value = '';
      this.inputs.creditedPurchaseId.selectValues = [NO_INVOICE];
      this.inputs.notes.value = '';
      Object.values(this.inputs).forEach(input => input.error = false);
      this.lines = [this.newLine()];
      this.creditedLines = [];
      this.showLines = false;
      this.$refs.linesEditor?.reset();
      this.$refs.allocationEditor?.reset();
    },
    // presetOrderId : dossier proposé d'office pour un nouvel achat (création depuis la fiche d'un dossier)
    // options : {creditNoteOf: facture} pour un avoir sur cette facture, {documentType} pour un avoir libre
    async openModal(purchase, presetOrderId, {documentType, creditNoteOf} = {}) {
      this.resetState(creditNoteOf ? CREDIT_NOTE.value : (documentType ?? INVOICE.value));
      this.$refs.modal.openModal();

      const [, , vatRegimes] = await Promise.all([this.loadSuppliers(), this.loadOrders(), loadVatRegimes()]);
      this.vatRegimes = vatRegimes;
      if (purchase) {
        await this.loadPurchase(idOf(purchase));
        return;
      }
      if (creditNoteOf) await this.prefillCreditNote(creditNoteOf);
      if (presetOrderId && this.hasProduction) this.$refs.allocationEditor?.addRow({orderId: String(presetOrderId)});
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    async loadSuppliers() {
      const suppliers = await SupplierController.getAll();
      this.suppliers = suppliers && !suppliers.errors ? suppliers : [];
      this.inputs.supplierId.selectValues = this.suppliers
          .map(supplier => ({label: supplier.name, value: idOf(supplier)}))
          .sort((a, b) => a.label.localeCompare(b.label));
    },
    // Dossiers (ms-production) pour la ventilation de l'achat, seulement si l'option production est active
    async loadOrders() {
      const orderValues = await loadOrderValues();
      this.hasProduction = orderValues !== undefined;
      this.orderValues = orderValues ?? [];
    },
    // Factures du fournisseur choisi, proposées à un avoir
    async loadInvoices() {
      if (!this.isCreditNote) return;
      const supplierId = this.inputs.supplierId.value;
      if (!supplierId) return;

      const rows = await PurchaseController.getBySupplier(supplierId);
      const invoices = rows && !rows.errors ? rows.filter(row => row.documentType !== CREDIT_NOTE.value) : [];
      this.inputs.creditedPurchaseId.selectValues = [NO_INVOICE, ...invoices
          .sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate))
          .map(invoice => ({label: `${invoice.reference || invoice.label} - ${formatDate(invoice.invoiceDate)} (${formatAmount(invoice.ttcAmount)})`, value: idOf(invoice)}))];
      // Une facture d'un autre fournisseur ne peut pas être visée
      if (!this.inputs.creditedPurchaseId.selectValues.some(item => item.value === this.inputs.creditedPurchaseId.value)) this.inputs.creditedPurchaseId.value = '';
    },
    // Articles de la facture visée : un avoir peut en rendre une partie
    async loadCreditedLines() {
      const invoiceId = this.inputs.creditedPurchaseId.value;
      this.creditedLines = [];
      if (!invoiceId) return;

      const invoice = await PurchaseController.get(invoiceId);
      if (invoice && !invoice.errors && this.inputs.creditedPurchaseId.value === invoiceId) this.creditedLines = invoice.lines ?? [];
    },
    async prefillCreditNote(invoice) {
      this.autoFill = false;
      this.inputs.supplierId.value = String(invoice.supplierId);
      this.inputs.category.value = invoice.category ?? this.inputs.category.value;
      this.inputs.label.value = `Avoir sur ${invoice.reference ? `facture ${invoice.reference}` : invoice.label}`;
      await this.loadInvoices();
      this.inputs.creditedPurchaseId.value = idOf(invoice);
    },
    async loadPurchase(id) {
      const purchase = await PurchaseController.get(id);
      if (!purchase || purchase.errors) {
        this.closeModal(true);
        return;
      }

      this.autoFill = false;
      await this.applyPurchase(purchase);
      await Promise.all([this.loadDisbursements(), this.loadReceptions(), this.loadCreditNotes()]);
    },
    async applyPurchase(purchase) {
      this.setDocumentType(purchase.documentType ?? INVOICE.value);
      this.purchaseId = idOf(purchase);
      this.purchase = {...purchase, id: idOf(purchase)};
      this.inputs.supplierId.value = String(purchase.supplierId);
      this.inputs.reference.value = purchase.reference ?? '';
      this.inputs.label.value = purchase.label ?? '';
      this.inputs.invoiceDate.value = toInputDate(purchase.invoiceDate);
      this.inputs.dueDate.value = toInputDate(purchase.dueDate);
      this.inputs.category.value = purchase.category;
      this.inputs.creditedPurchaseId.value = purchase.creditedPurchaseId ? String(purchase.creditedPurchaseId) : '';
      this.inputs.notes.value = purchase.notes ?? '';

      this.lines = (purchase.vatLines ?? []).map(line => this.newLine({rate: line.rate, htAmount: line.htAmount, vatAmount: line.vatAmount}));
      if (this.lines.length === 0) this.lines = [this.newLine()];

      this.$refs.linesEditor?.load(purchase.lines ?? []);
      this.showLines = (purchase.lines ?? []).length > 0;

      // Un dossier qui n'est plus dans la liste reste affiché avec les informations enregistrées sur l'achat
      (purchase.allocations ?? []).forEach(allocation => {
        const orderId = String(allocation.orderId);
        if (!this.orderValues.some(order => order.value === orderId)) {
          const title = allocation.orderTitle ? ` - ${allocation.orderTitle}` : '';
          this.orderValues.push({label: `N°${allocation.orderNumber ?? '?'}${title}`, value: orderId});
        }
      });
      // Les parts de ventilation s'appuient sur les articles : l'éditeur de ventilation doit les avoir reçus avant de charger les parts
      await this.$nextTick();
      this.$refs.allocationEditor?.load(purchase.allocations ?? []);
    },
    async loadDisbursements() {
      const disbursements = await DisbursementController.getByPurchase(this.purchaseId);
      this.disbursements = disbursements && !disbursements.errors
          ? disbursements.sort((a, b) => new Date(b.date) - new Date(a.date))
          : [];
    },
    async loadReceptions() {
      if (!this.purchase.deliveryStatus) return;
      const receptions = await ReceptionController.getByPurchase(this.purchaseId);
      this.receptions = receptions && !receptions.errors
          ? receptions.sort((a, b) => new Date(b.date) - new Date(a.date))
          : [];
    },
    async loadCreditNotes() {
      if (this.isCreditNote) return;
      const creditNotes = await PurchaseController.getByInvoice(this.purchaseId);
      this.creditNotes = creditNotes && !creditNotes.errors
          ? creditNotes.sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate))
          : [];
    },
    // L'achat enregistré a changé ailleurs (paiement, réception, avoir) : statut, montants payés et livraison à jour, le formulaire reste tel quel
    async refreshPurchase() {
      const purchase = await PurchaseController.get(this.purchaseId);
      if (purchase && !purchase.errors) this.purchase = {...purchase, id: idOf(purchase)};
      if (this.callback) this.callback();
    },
    supplierName() {
      return this.suppliers.find(item => idOf(item) === this.inputs.supplierId.value)?.name ?? '';
    },
    download() {
      return downloadIncomingInvoice({...this.purchase, supplierName: this.supplierName()});
    },
    pay() {
      this.$refs.disbursementModal.openModal({purchase: {
        id: this.purchaseId,
        documentType: this.documentType,
        supplierName: this.supplierName(),
        label: this.purchase.label,
        reference: this.purchase.reference,
        remainingAmount: this.remainingAmount
      }});
    },
    editDisbursement(disbursement) {
      this.$refs.disbursementModal.openModal({disbursement});
    },
    // Un paiement a changé : le statut et le payé de l'achat aussi (et les avoirs compensés), le tableau parent doit se recharger
    async onDisbursementChanged() {
      await this.refreshPurchase();
      await Promise.all([this.loadDisbursements(), this.loadCreditNotes()]);
    },
    receive() {
      this.$refs.receptionModal.openModal({purchaseId: this.purchaseId, supplierName: this.supplierName()});
    },
    editReception(reception) {
      this.$refs.receptionModal.openModal({reception: {...reception, supplierName: this.supplierName()}});
    },
    async onReceptionChanged() {
      await this.refreshPurchase();
      await this.loadReceptions();
    },
    receptionSummary(reception) {
      return reception.lines.map(line => formatQuantity(line.quantity, line.unit)).join(' · ');
    },
    // Un avoir sur cette facture : la modale passe en création d'avoir
    createCreditNote() {
      return this.openModal(undefined, undefined, {creditNoteOf: {...this.purchase, supplierId: this.inputs.supplierId.value}});
    },
    onProofsChanged(proofs) {
      this.purchase = {...this.purchase, proofs};
      if (this.callback) this.callback();
    },
    async onSupplierCreated(supplierId) {
      await this.loadSuppliers();
      this.inputs.supplierId.value = supplierId;
    },
    fail(input, message) {
      if (input) input.error = true;
      (new NotificationService).addNotification("danger", message);
    },
    // Lignes de TVA saisies, sans les lignes laissées vides
    buildVatLines() {
      return this.lines
          .filter(line => !this.isBlank(line.htAmount.value))
          .map(line => ({
            rate: this.toNumber(line.rate.value),
            htAmount: this.toNumber(line.htAmount.value),
            vatAmount: this.isBlank(line.vatAmount.value) ? undefined : this.toNumber(line.vatAmount.value)
          }));
    },
    async submitForm(validateAfter = false) {
      if (!this.inputs.supplierId.value) return this.fail(this.inputs.supplierId, "Le fournisseur n'est pas renseigné");
      if (!this.inputs.invoiceDate.value) return this.fail(this.inputs.invoiceDate, `La date ${this.isCreditNote ? "de l'avoir" : 'de la facture'} n'est pas renseignée`);

      // Avec des articles, le montant en découle ; sinon il vient des lignes de TVA saisies
      const lines = this.linesState.count > 0 ? this.$refs.linesEditor.getValue() : [];
      if (lines === undefined) return;
      const vatLines = lines.length === 0 ? this.buildVatLines() : undefined;
      if ((vatLines && vatLines.length === 0) || !(this.displayTotals.ttc > 0)) return this.fail(undefined, "Le montant n'est pas renseigné");

      let allocations;
      if (this.hasProduction) {
        allocations = this.$refs.allocationEditor.getValue();
        if (allocations === undefined) return;
      }

      const reference = String(this.inputs.reference.value ?? '').trim();
      const dueDate = this.inputs.dueDate.value;
      const body = {
        supplierId: this.inputs.supplierId.value,
        reference,
        label: String(this.inputs.label.value ?? '').trim() || reference || this.supplierName(),
        category: this.inputs.category.value,
        invoiceDate: this.inputs.invoiceDate.value,
        notes: String(this.inputs.notes.value ?? '').trim(),
      };
      if (lines.length > 0) body.lines = lines;
      else {
        body.vatLines = vatLines;
        // Des articles retirés sont effacés de l'achat enregistré
        if (this.isEdit && (this.purchase.lines ?? []).length > 0) body.lines = [];
      }
      if (allocations !== undefined) body.allocations = allocations;
      if (this.isCreditNote) {
        // En modification, la facture visée peut être retirée
        const creditedPurchaseId = this.inputs.creditedPurchaseId.value;
        if (this.isEdit) body.creditedPurchaseId = creditedPurchaseId || null;
        else if (creditedPurchaseId) body.creditedPurchaseId = creditedPurchaseId;
      } else {
        // En modification, une échéance vidée est effacée ; en création, le délai du fournisseur s'applique
        body.dueDate = this.isEdit ? (dueDate || null) : (dueDate || undefined);
      }
      if (!this.isEdit && this.isCreditNote) body.documentType = CREDIT_NOTE.value;

      this.buttons.validate.disabled = true;
      const res = await this.$refs.form.submitForm(async () => {
        const saved = this.isEdit ? await PurchaseController.patch(this.purchaseId, body) : await PurchaseController.post(body);
        if (saved.errors || !validateAfter) return saved;
        return await PurchaseController.validate(this.isEdit ? this.purchaseId : saved.id);
      });
      this.buttons.validate.disabled = false;

      if (!res.errors) {
        (new NotificationService).addNotification("success", `${this.isCreditNote ? 'Avoir' : 'Achat'} enregistré${validateAfter ? ' et validé' : ''}`);
        this.closeModal(true);
        if (this.callback) this.callback();
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/purchase/style/section.styl';

.vat-note
  margin 0 0 10px
  padding 8px 10px
  border-radius 8px
  background #eef4ff
  color #34507d
  font-size 13px

  .fas
    margin-right 6px

.container
  display flex
  flex-direction column
  gap 10px

  .summary
    display flex
    align-items center
    flex-wrap wrap
    gap 6px 14px

    .link-button
      margin 0

    .source
      color #6b7280
      font-size 0.9em

    .paid
      margin-left auto
      color #6b7280
      font-size 0.9em

  .review-note
    margin 0
    padding 8px 10px
    border-radius 6px
    background #fff3cd
    font-size 0.85em

  form
    display flex
    flex-direction column
    gap 4px

    .supplier-row
      display flex
      align-items flex-end
      gap 8px

      .supplier
        flex 1
        min-width 0

      .add-supplier
        margin-right 0
        margin-bottom 2px

    .fields-row
      display grid
      grid-template-columns 1fr 1fr
      gap 12px

    .section .readonly-allocation
      margin 2px 0
      font-size 0.9em

    .line-row
      display grid
      grid-template-columns 1fr 1fr 1fr 28px
      align-items end
      gap 10px

      &.line-header
        font-size 0.8em
        font-weight 600
        color #6b7280

    .mini-table
      width 100%
      border-collapse collapse
      font-size 0.9em

      tr
        cursor pointer

        &:hover
          background-color rgba(0, 0, 0, 0.04)

      td
        padding 4px 8px
        white-space nowrap

        &.fill
          width 100%
          color #6b7280

        &.amount
          text-align right
          font-weight 600

    // Le bouton principal est en premier dans le DOM (action par défaut de la touche Entrée),
    // l'ordre visuel est inversé : [Annuler] [Enregistrer et valider] [Enregistrer]
    .button-container
      display flex
      flex-direction row-reverse
      justify-content flex-start
      gap 10px
      margin-top 14px

      .button
        min-width 110px

      .validate
        min-width 170px

@media (max-width 600px)
  .container form
    .fields-row
      grid-template-columns 1fr

    .line-row
      grid-template-columns 1fr 1fr 1fr 28px
</style>

<style lang="stylus">
.purchase-modal
  > .modal
    width 780px !important
    max-width 100%
    height auto !important
    max-height 92vh
    overflow-y auto
</style>
